import { Injectable } from '@angular/core';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  ObjectTypeInfoViewModel,
  QueryColumnSortOrder,
  QueryObjectsModel,
  QueryOutputMode,
  ReadDocumentModel,
  SubQueryKind,
} from '@barthauer/abc/core/data';
import { AbcExpressionsEditorService } from '@barthauer/abc/queries/opath/expressions-editor';
import { _AbcTreeNode } from '@barthauer/abc/ui-components';

import { ColumnTypeIcon } from '@barthauer/abc/queries/opath/core';
import { ParameterDefinition } from '@barthauer/abc/queries/parameters/core';
import {
  EditorInstanceSettings,
  ExpressionEditorResult,
  ExpressionEditorSettings,
  ValidationType,
} from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { BehaviorSubject } from 'rxjs';
import { AbcQueriesEditorService } from '../abc-queries-editor.service';
import {
  ColumnProperties,
  ColumnSpacing,
  Property,
  QueryProperties,
  SideBarButtonId,
} from '../viewModels/abc-queries-editor.viewModel';

@Injectable({
  providedIn: 'root',
})
export class QueriesTreeService {
  public expressionEditorInsertion = new BehaviorSubject<_AbcTreeNode<Property>[]>(null);
  public expressionEditorValueChanged = new BehaviorSubject<void | ExpressionEditorResult>(null);
  constructor(
    private abcCore: AbcCore,
    private abcExpressionsEditorService: AbcExpressionsEditorService,
    private abcQueriesEditorService: AbcQueriesEditorService,
    private coreDataService: CoreDataService
  ) {}

  public async readQueryFromDocument(queryDocumentId: string): Promise<QueryObjectsModel> {
    const queryDocument = new ReadDocumentModel();
    queryDocument.Id = queryDocumentId;
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.IncludeUnusedParameters = true;
    return ConvertHelper.arrayBufferToObject(
      await this.coreDataService.executeReadQueryFromDocumentQuery(queryDocument).toPromise()
    );
  }

  public async openOpathExpression(
    validationType: ValidationType,
    node: _AbcTreeNode<Property>,
    parameterDefinitions: ParameterDefinition[],
    action?: SideBarButtonId,
    popUpTitle?: string
  ): Promise<void> {
    const newNode = new _AbcTreeNode<Property>();
    newNode.data = new Property();

    let hostingNode: _AbcTreeNode<Property> = new _AbcTreeNode<Property>();

    if (action) {
      hostingNode = node as _AbcTreeNode<Property>;
    } else {
      if (node.parentNode && node.parentNode.parentId !== null) {
        hostingNode = node.parentNode as _AbcTreeNode<Property>;
      } else {
        hostingNode = node as _AbcTreeNode<Property>;
      }
    }

    const otvm: ObjectTypeInfoViewModel = await this.abcCore.objectSpace
      .getObjectType(hostingNode.data.name)
      .toPromise();

    const expressionsEditorResultAction = async (expressionResult: ExpressionEditorResult) => {
      if (expressionResult && this.abcQueriesEditorService.queryDocument) {
        this.abcQueriesEditorService.queryDocument.ParameterDefinitions = expressionResult.parameterDefinitions;
      }
      if (validationType === ValidationType.Column && expressionResult) {
        if (action && action === SideBarButtonId.INSERT_COLUMN) {
          newNode.caption = expressionResult.columnTitle || expressionResult.oPath.split('.').join('');
          newNode.icon = ColumnTypeIcon.box;
          newNode.data.expression = expressionResult.oPath;
          newNode.data.alias = newNode.caption;
          newNode.data.name = expressionResult.oPath;
          newNode.id = hostingNode.id + `_` + this.generatePropertyId(expressionResult.oPath, hostingNode);
          newNode.parentId = node.id;
          newNode.data.columnProperties = new ColumnProperties({
            visible: true,
            sorting: QueryColumnSortOrder.None,
            spacing: ColumnSpacing.IGNOR_BLANK_PROPERTIES,
            culture: false,
            filter: false,
            formatExpression: null,
          });
          newNode.children = [];
          this.expressionEditorInsertion.next([newNode]);
        } else {
          node.data.expression = expressionResult.oPath;
          node.caption = expressionResult.columnTitle || expressionResult.oPath.split('.').join('');
          this.expressionEditorValueChanged.next();
        }
      } else if (validationType === ValidationType.Query && expressionResult && expressionResult.oPath.length > 0) {
        if (action && action === SideBarButtonId.INSERT_SUB_QUERY) {
          newNode.caption = expressionResult.columnTitle || expressionResult.oPath.split('.').join('');
          newNode.icon = ColumnTypeIcon.reference;
          newNode.data.expression = expressionResult.oPath;
          newNode.data.alias = newNode.caption;
          newNode.data.name = expressionResult.validate.RefObjectTypeName;
          newNode.id = hostingNode.id + `_` + this.generatePropertyId(expressionResult.oPath, hostingNode);
          newNode.parentId = node.id;
          newNode.data.queryProperties = new QueryProperties({
            skippedRowsNbr: 0,
            limitRowsNbr: 0,
            isDuplicated: false,
            subQueryKind: SubQueryKind.Sub,
          });
          newNode.children = [];
          this.expressionEditorInsertion.next([newNode]);
        } else {
          this.expressionEditorValueChanged.next(expressionResult);
        }
      }
    };

    const settings: ExpressionEditorSettings = new ExpressionEditorSettings();
    settings.editorInstanceSettings = new EditorInstanceSettings({
      editorId: '',
      initialValue: '',
      expressionsEditorResultAction: expressionsEditorResultAction,
    });

    settings.objectTypeInfoViewModels = [otvm];
    settings.parameterDefinitions = parameterDefinitions;
    settings.validationType = validationType;

    if (validationType === ValidationType.Column) {
      if (action && action === SideBarButtonId.INSERT_COLUMN) {
        settings.oPath = null;
        settings.columnTitle = null;
        settings.editorInstanceSettings.initialValue = null;
      } else {
        settings.oPath = node.data.expression;
        settings.columnTitle = node.caption;
        settings.editorInstanceSettings.initialValue = node.data.expression;
      }
    } else if (validationType === ValidationType.Query) {
      if (action && action === SideBarButtonId.INSERT_SUB_QUERY) {
        settings.oPath = null;
        settings.columnTitle = null;
        settings.editorInstanceSettings.initialValue = null;
      } else {
        settings.oPath = node.data.expression;
        settings.columnTitle = node.caption;
        settings.editorInstanceSettings.initialValue = node.data.expression;
      }
    }

    settings.title = popUpTitle ? popUpTitle : node.caption;

    this.abcExpressionsEditorService.openExpressionEditor(settings);
  }

  public generatePropertyId = (nodeId: string, hostingNode: _AbcTreeNode<Property>): string => {
    let suffix = 0;

    if (hostingNode.children.find((node: _AbcTreeNode<Property>) => node.id.includes(nodeId))) {
      suffix++;
      while (hostingNode.children.find((node: _AbcTreeNode<Property>) => node.id.includes(`${nodeId}-${suffix}`))) {
        suffix++;
      }
    }

    return suffix ? nodeId + `-${suffix}` : nodeId;
  };
}
